import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";
import { Users, Crown, TrendingUp, FileText, Wallet, Bot, BarChart3, ImageIcon, Trophy, RotateCcw, MessageSquare, Settings, Tag, ListChecks, Rocket, BookOpen, ShieldCheck, Receipt, Database, Shield, Award } from "lucide-react";
import api, { apiErr } from "../lib/api";
import AdminGenerator from "./AdminGenerator";
import AdminQuestions from "./AdminQuestions";
import AdminApprovals from "./AdminApprovals";
import AdminFeedback from "./AdminFeedback";
import AdminContact from "./AdminContact";
import AdminProducts from "./AdminProducts";
import AdminPackages from "./AdminPackages";
import AdminMega from "./AdminMega";
import AdminModules from "./AdminModules";
import AdminGuilds from "./AdminGuilds";
import AdminAudit from "./AdminAudit";
import AdminTransactions from "./AdminTransactions";
import AdminRevenue from "./AdminRevenue";
import AdminSuccessStories from "./AdminSuccessStories";
import Btn from "../components/Btn";

const fmt = (n) => "Rp" + Number(n || 0).toLocaleString("id-ID");

const TABS = [
  { id: "analytics", label: "Analitik", icon: BarChart3 },
  { id: "packages", label: "Tryout", icon: ListChecks },
  { id: "mega", label: "Akbar", icon: Rocket },
  { id: "modules", label: "Modul", icon: BookOpen },
  { id: "guilds", label: "Guild", icon: Shield },
  { id: "products", label: "Produk VIP", icon: Tag },
  { id: "transactions", label: "Transaksi", icon: Receipt },
  { id: "revenue", label: "Laporan Pendapatan", icon: Wallet },
  { id: "generator", label: "Generator Soal AI", icon: Bot },
  { id: "questions", label: "Bank Soal", icon: Database },
  { id: "approvals", label: "Verifikasi Bukti", icon: ImageIcon },
  { id: "feedback", label: "Saran & Bug", icon: MessageSquare },
  { id: "stories", label: "Testimoni", icon: Award },
  { id: "audit", label: "Log Aktivitas", icon: ShieldCheck },
  { id: "settings", label: "Pengaturan", icon: Settings },
];

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("analytics");
  const [pendingProofs, setPendingProofs] = useState(0);

  useEffect(() => {
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/admin/share-submissions?status=pending").then(({ data }) => setPendingProofs(data.submissions.length)).catch(() => {});
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading font-black text-2xl sm:text-3xl">Admin CMS</h1>
        <p className="text-su-muted font-medium">Kelola konten, soal AI, & pantau performa SkorUp.io</p>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-1">
        {TABS.map((t) => (
          <button key={t.id} data-testid={`admin-tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`shrink-0 inline-flex items-center gap-2 px-4 py-2.5 rounded-2xl font-heading font-extrabold text-sm border-2 transition-all ${
              tab === t.id ? "bg-su-primary text-white border-su-primaryShadow" : "bg-su-surface text-su-muted border-su-border"
            }`}>
            <t.icon className="w-4 h-4" /> {t.label}
            {t.id === "approvals" && pendingProofs > 0 && (
              <span data-testid="approvals-badge"
                className="ml-0.5 grid place-items-center min-w-[20px] h-5 px-1.5 rounded-full bg-su-danger text-white text-[11px] font-heading font-black leading-none">
                {pendingProofs > 99 ? "99+" : pendingProofs}
              </span>
            )}
          </button>
        ))}
      </div>

      {tab === "analytics" && (!stats ? (
        <div className="min-h-[40vh] grid place-items-center"><div className="w-12 h-12 rounded-full border-4 border-su-border border-t-su-primary animate-spin" /></div>
      ) : (
        <Analytics stats={stats} />
      ))}
      {tab === "generator" && <AdminGenerator />}
      {tab === "questions" && <AdminQuestions />}
      {tab === "packages" && <AdminPackages />}
      {tab === "mega" && <AdminMega />}
      {tab === "modules" && <AdminModules />}
      {tab === "guilds" && <AdminGuilds />}
      {tab === "products" && <AdminProducts />}
      {tab === "transactions" && <AdminTransactions />}
      {tab === "revenue" && <AdminRevenue />}
      {tab === "stories" && <AdminSuccessStories />}
      {tab === "approvals" && <AdminApprovals onCountChange={setPendingProofs} />}
      {tab === "feedback" && <AdminFeedback />}
      {tab === "audit" && <AdminAudit />}
      {tab === "settings" && <AdminContact />}
    </div>
  );
}

function Analytics({ stats }) {
  const cards = [
    { icon: Users, color: "var(--su-primary)", label: "Total Pengguna", value: stats.total_users },
    { icon: Crown, color: "var(--su-secondaryShadow)", label: "Pengguna VIP", value: stats.vip_users },
    { icon: TrendingUp, color: "var(--su-success)", label: "Konversi VIP", value: `${stats.conversion_rate}%` },
    { icon: FileText, color: "var(--su-energy)", label: "Tryout Selesai", value: stats.total_attempts },
    { icon: Wallet, color: "#a855f7", label: "Pendapatan", value: fmt(stats.revenue) },
    { icon: Bot, color: "var(--su-primary)", label: "Bank Soal", value: stats.question_count },
  ];
  const chartData = [
    { name: "Users", v: stats.total_users },
    { name: "VIP", v: stats.vip_users },
    { name: "Tryout", v: stats.total_attempts },
    { name: "Paid", v: stats.paid_transactions },
  ];
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {cards.map((c) => (
          <div key={c.label} className="card-chunky p-5" data-testid={`admin-stat-${c.label}`}>
            <div className="w-11 h-11 rounded-xl grid place-items-center mb-3" style={{ background: `${c.color}22` }}><c.icon className="w-6 h-6" style={{ color: c.color }} /></div>
            <p className="text-xs font-heading font-black uppercase text-su-muted">{c.label}</p>
            <p className="font-heading font-black text-2xl">{c.value}</p>
          </div>
        ))}
      </div>
      <div className="card-chunky p-6">
        <h2 className="font-heading font-black text-lg mb-4">Ringkasan Platform</h2>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--su-border)" />
              <XAxis dataKey="name" tick={{ fill: "var(--su-muted)", fontWeight: 700 }} />
              <YAxis tick={{ fill: "var(--su-muted)" }} />
              <Tooltip contentStyle={{ background: "var(--su-surface)", border: "2px solid var(--su-border)", borderRadius: 16 }} />
              <Bar dataKey="v" fill="var(--su-primary)" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <SeasonCard />
    </div>
  );
}

function SeasonCard() {
  const [season, setSeason] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/season").then(({ data }) => setSeason(data)).catch(() => {}); }, []);
  const reset = async () => {
    if (!window.confirm("Reset musim? Semua pengguna dapat lencana + token sesuai tier, lalu ELO direset ke 1000.")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/season/reset");
      toast.success(`Musim ${data.new_season} dimulai! ${data.rewarded} pengguna diberi hadiah.`);
      setSeason((s) => ({ ...s, season: data.new_season }));
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="card-chunky p-6" data-testid="admin-season">
      <div className="flex items-center gap-2 mb-2"><Trophy className="w-5 h-5 text-su-secondary" /><h2 className="font-heading font-black text-lg">Musim Arena</h2></div>
      <p className="text-su-muted font-medium text-sm mb-4">Musim saat ini: <span className="font-heading font-black text-su-text">Season {season?.season ?? "-"}</span>. Reset musim membagikan lencana tier & token bonus ke semua pengguna, lalu mengembalikan ELO ke 1000.</p>
      <Btn variant="danger" data-testid="season-reset" disabled={busy} onClick={reset}><RotateCcw className="w-4 h-4" /> {busy ? "Memproses..." : "Reset Musim & Bagikan Hadiah"}</Btn>
    </div>
  );
}
